import {Component, OnInit, ViewChild, AfterViewInit, isDevMode, Input, SimpleChanges, OnChanges, OnDestroy} from '@angular/core';
import { Router } from "@angular/router";
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { CentreService } from 'src/app/services/centre.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { EditRemarkDialogComponent } from '../edit-remark-dialog/edit-remark-dialog.component';
import {PermissionType} from "../../../../../components/employee/PermissionType.enum";
import {UserRole} from "../../../../../components/rename/add-user-role/add-user-role.component";
import {FinancialYearService} from "../../../../../services/financial-year.service";

@Component({
  selector: 'app-counselling-highlight-list',
  templateUrl: './counselling-highlight-list.component.html',
  styleUrls: ['./counselling-highlight-list.component.scss']
})
export class CounsellingHighlightListComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('childPaginator') childPaginator: MatPaginator | null = null;
  @ViewChild('teenPaginator') teenPaginator: MatPaginator | null = null;
  @ViewChild('caregiverPaginator') caregiverPaginator: MatPaginator | null = null;
  @ViewChild('childSort') childSort: MatSort | null = null;
  @ViewChild('teenSort') teenSort: MatSort | null = null;
  @ViewChild('caregiverSort') caregiverSort: MatSort | null = null;
  // ── NOTE: removed the duplicate unnamed @ViewChild(MatPaginator) and
  //          @ViewChild(MatSort) that were shadowing the named ones above ──────

  @Input() selectedCentreCode: string | null = 'All';
  @Input() selectedCentreCodes: string[] = [];
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() isNestedComponent: boolean = false;

  centreControl = new FormControl<string[]>([]);
  sessionTypeControl = new FormControl('ALL');
  sessionTypeFilter = new FormControl('');

  types = ["Admission Report building counselling", "Regular Counselling", "Discharge Counselling", "Follow-up Counselling"];
  childColumns: string[] = ['centreName', 'childName', 'dateRange', 'sessionType', 'challenges', 'remark'];
  teenColumns: string[] = ['centreName', 'childName', 'dateRange', 'sessionType', 'challenges', 'remark'];
  caregiverColumns: string[] = ['centreName', 'childName', 'dateRange', 'sessionType', 'relation', 'challenges', 'remark'];

  childDataSource = new MatTableDataSource<any>([]);
  teenDataSource = new MatTableDataSource<any>([]);
  caregiverDataSource = new MatTableDataSource<any>([]);

  centres$: Observable<any[]> | undefined;
  loading = false;
  env = isDevMode() ? environment.testMode : environment.productionMode;

  childCount = 0;
  teenCount = 0;
  caregiverCount = 0;

  selectedCentreObjects: any[] = [];
  expandedRemarks: { [sessionId: string]: boolean } = {};

  private searchCancel$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private readonly afs: AngularFirestore,
    private readonly centreService: CentreService,
    private readonly financialYearService: FinancialYearService,
    private dialog: MatDialog
  ) {}


  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isNestedComponent) return;

    const centreChange = changes['selectedCentreCodes'] || changes['selectedCentreCode'];
    const startChange  = changes['startDate'];
    const endChange    = changes['endDate'];

    if (!centreChange && !startChange && !endChange) return;

    const effectiveStart = startChange ? startChange.currentValue : this.startDate;
    const effectiveEnd   = endChange   ? endChange.currentValue   : this.endDate;

    if (!effectiveStart || !effectiveEnd) return;

    this.search();
  }

  ngOnInit(): void {
    if (!this.isNestedComponent) {
      this.centres$ = this.centreService.getAllCentresOptions() as Observable<any[]>;
      // Pre-fill the date range picker with the current financial year,
      // but don't apply it until the user clicks Search.
      const fy = this.financialYearService.getCurrentFinancialYear();
      this.startDate = fy.startDate;
      this.endDate = fy.endDate;
      this.search(true);
      return;
    }
    this.search();
  }

  ngAfterViewInit(): void {
    // Because ng-template wrappers are removed, all paginators/sorts are
    // available in the DOM immediately — these assignments will always succeed.
    this.childDataSource.paginator = this.childPaginator;
    this.childDataSource.sort = this.childSort;
    this.teenDataSource.paginator = this.teenPaginator;
    this.teenDataSource.sort = this.teenSort;
    this.caregiverDataSource.paginator = this.caregiverPaginator;
    this.caregiverDataSource.sort = this.caregiverSort;
  }

  ngOnDestroy(): void {
    this.searchCancel$.next();
    this.searchCancel$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }


  onCentresSelected(selected: any[]) {
    this.selectedCentreObjects = selected || [];
    const codes = (this.selectedCentreObjects || [])
      .map(s => (typeof s === 'string' ? s : s?.centreCode))
      .filter(Boolean);
    this.centreControl.setValue(codes);
  }


  search(skipDateFilter: boolean = false) {
    this.searchCancel$.next();
    this.loading = true;

    let selectedCentres: string[];
    if (this.isNestedComponent) {
      if (this.selectedCentreCodes && this.selectedCentreCodes.length > 0) {
        selectedCentres = this.selectedCentreCodes.includes('All Centres') ? [] : this.selectedCentreCodes;
      } else {
        selectedCentres = this.selectedCentreCode === 'All' || !this.selectedCentreCode
          ? []
          : [this.selectedCentreCode];
      }
    } else {
      selectedCentres = this.centreControl.value || [];
    }

    const selectedSessionType = this.sessionTypeFilter.value;
    const counsellingPath = `${this.env.counseling}/allSessions/sessions`;

    const query = this.afs.collection<any>(counsellingPath, ref =>
      ref.where('isHighlight', '==', true)
    );

    query.get().pipe(
      takeUntil(this.searchCancel$)
    ).subscribe({
      next: (snapshot) => {
        let sessions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        if (!skipDateFilter && (this.startDate || this.endDate)) {
          sessions = sessions.filter(s => {
            const sessionDate = s.date?.toDate ? s.date.toDate() : new Date(s.date);
            if (this.startDate && this.endDate) {
              const start = new Date(this.startDate); start.setHours(0, 0, 0, 0);
              const end   = new Date(this.endDate);   end.setHours(23, 59, 59, 999);
              return sessionDate >= start && sessionDate <= end;
            }
            if (this.startDate) {
              const start = new Date(this.startDate); start.setHours(0, 0, 0, 0);
              return sessionDate >= start;
            }
            if (this.endDate) {
              const end = new Date(this.endDate); end.setHours(23, 59, 59, 999);
              return sessionDate <= end;
            }
            return true;
          });
        }

        if (selectedCentres.length > 0) {
          sessions = sessions.filter(s => selectedCentres.includes(s.centreCode));
        }

        if (selectedSessionType && selectedSessionType !== '') {
          sessions = sessions.filter(s => s.type === selectedSessionType);
        }

        sessions.sort((a, b) => (b.date?.toDate() || 0) - (a.date?.toDate() || 0));

        const childSessions: any[] = [];
        const teenSessions: any[] = [];
        const caregiverSessions: any[] = [];

        sessions.forEach(session => {
          const categories = this.categorizeSession(session);
          if (categories.includes('CHILD'))     childSessions.push(session);
          if (categories.includes('TEEN'))      teenSessions.push(session);
          if (categories.includes('CAREGIVER')) caregiverSessions.push(session);
        });

        this.childDataSource.data     = childSessions;
        this.teenDataSource.data      = teenSessions;
        this.caregiverDataSource.data = caregiverSessions;

        this.childCount     = childSessions.length;
        this.teenCount      = teenSessions.length;
        this.caregiverCount = caregiverSessions.length;
        this.loading        = false;
      },
      error: (err) => {
        console.error('Error fetching highlighted sessions:', err);
        this.loading = false;
      }
    });
  }

  clearFilters() {
    this.centreControl.setValue([]);
    this.selectedCentreObjects = [];
    if (!this.isNestedComponent) {
      const fy = this.financialYearService.getCurrentFinancialYear();
      this.startDate = fy.startDate;
      this.endDate = fy.endDate;
    } else {
      this.startDate = null;
      this.endDate = null;
    }
    this.sessionTypeControl.setValue('ALL');
    this.sessionTypeFilter.setValue('');
    this.childCount = 0;
    this.teenCount = 0;
    this.caregiverCount = 0;
    this.search(true);
  }


  private categorizeSession(session: any): Array<'CHILD' | 'TEEN' | 'CAREGIVER'> {
    if (!session.participants || !Array.isArray(session.participants)) return ['CAREGIVER'];
    const participants: string[] = session.participants;
    const categories: Array<'CHILD' | 'TEEN' | 'CAREGIVER'> = [];
    if (participants.some(p => p.toLowerCase().includes('teen')))  categories.push('TEEN');
    if (participants.some(p => p.toLowerCase().includes('child'))) categories.push('CHILD');
    if (participants.some(p => !p.toLowerCase().includes('child') && !p.toLowerCase().includes('teen'))) {
      categories.push('CAREGIVER');
    }
    return categories.length > 0 ? categories : ['CAREGIVER'];
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-GB');
  }

  getDateRange(session: any): string  { return this.formatDate(session.date); }
  getSessionType(session: any): string { return session.type || 'N/A'; }

  getRelation(session: any): string {
    if (!session.participants || !Array.isArray(session.participants)) return 'N/A';
    const relationMap: { [key: string]: string } = {
      'Father (Males)': 'Father', 'Mother (Females)': 'Mother',
      'Brother': 'Sibling',       'Sister': 'Sibling',
      'Grandfather': 'Grandparent', 'Grandmother': 'Grandparent'
    };
    const priority = ['Father (Males)', 'Mother (Females)', 'Brother', 'Sister', 'Grandfather', 'Grandmother'];
    for (const p of priority) if (session.participants.includes(p)) return relationMap[p];
    return session.participants[0] || 'N/A';
  }

  getChallenges(session: any): string {
    if (session.challengesFaced) {
      return Array.isArray(session.challengesFaced)
        ? session.challengesFaced.join(', ')
        : session.challengesFaced;
    }
    return 'N/A';
  }

  getRemark(session: any): string { return session.highlightRemark || 'No remark'; }

  toggleRemark(sessionId: string) {
    this.expandedRemarks[sessionId] = !this.expandedRemarks[sessionId];
  }

  openEditRemarkDialog(session: any) {
    this.dialog.open(EditRemarkDialogComponent, {
      width: '600px',
      data: {
        sessionId: session.id,
        currentRemark: session.highlightRemark || '',
        childName: session.childName || 'Unknown'
      }
    }).afterClosed().subscribe(result => {
      if (result?.updated) this.updateSessionRemark(session.id, result.remark);
    });
  }

  private updateSessionRemark(sessionId: string, newRemark: string) {
    this.afs.collection(`${this.env.counseling}/allSessions/sessions`)
      .doc(sessionId)
      .update({ highlightRemark: newRemark })
      .then(() => {
        this.updateLocalSessionRemark(this.childDataSource.data, sessionId, newRemark);
        this.updateLocalSessionRemark(this.teenDataSource.data, sessionId, newRemark);
        this.updateLocalSessionRemark(this.caregiverDataSource.data, sessionId, newRemark);
      });
  }

  private updateLocalSessionRemark(dataArray: any[], sessionId: string, newRemark: string) {
    const session = dataArray.find(s => s.id === sessionId);
    if (session) session.highlightRemark = newRemark;
  }

  isGranted(permissionType: PermissionType): boolean {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]') as UserRole[];
    const counsellingPermission = permissions.find(p => p.functionName === 'Counseling');
    if (!counsellingPermission) return false;
    switch (permissionType) {
      case PermissionType.ADD:     return (counsellingPermission as any).write;
      case PermissionType.EDIT:    return (counsellingPermission as any).read;
      case PermissionType.ARCHIVE: return (counsellingPermission as any).delete;
      default: return false;
    }
  }

  protected readonly PermissionType = PermissionType;
}
